/**
 * Play Archive (FR-ARCHIVE-01…05, AC-ARCH-001…005).
 *
 * The log of every play in an event — dense, fit to the screen, sortable and
 * filterable on any column — and the place a play gets corrected, inserted,
 * deleted or its conflicting submissions settled.
 *
 *   - Data: the whole event is loaded once (`useArchivePlays`); sort / filter
 *     run client-side on any column, held in the URL (`archive-query.ts`).
 *   - Columns: ONE registry (`archive-columns.ts`) built from the stat catalog
 *     + fixed situation/result columns + custom stats. The per-user column
 *     choice (`useColumnPrefs`) and the container width (`fitColumns`) decide
 *     what is on screen; the rest is in the row expander. Never a sideways
 *     scroll: a phone gets a card list instead.
 *   - Live: one subscription per event; conflict badges patch in place, any
 *     other change refetches.
 */
import { useCallback, useMemo, useRef, useState } from 'react';

import { useActiveEvent } from '../../app/active-event-context';
import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { useToast } from '../../app/toast-context';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { Segmented } from '../../components/segmented-control';
import type { PlayView, ResolveConflictInput } from '../../lib/api-types';
import { EVENT_REASON_MIN_LENGTH, STAR_COPY } from '../../lib/constants';
import {
  LIVE_MESSAGE,
  usePlayArchiveSubscription,
  type HealthSocketFactory,
  type PlayArchiveEvent,
} from '../../lib/live-client';
import { canFavoritePlays } from '../../lib/personas';
import { TableExportButton } from '../integrations/export-dialog';
import { PLAY_PHASES } from '../play-entry/play-catalog';
import { useCustomStatFields } from '../play-entry/use-custom-stat-fields';
import { useStatOptionLibrary } from '../play-entry/use-stat-option-library';

import {
  buildArchiveColumns,
  columnById,
  facetIndex,
  appliesTo,
  phaseOf,
  playcallText,
  situationText,
  tagLabels,
  timeText,
  type ArchiveColumn,
  type RegistryContext,
} from './archive-columns';
import { buildArchiveExportSource } from './archive-export';
import {
  ACTIONS_COLUMN_PX,
  canvasMeasurer,
  fitColumns,
  measureColumnWidths,
  toggleColumn,
} from './archive-layout';
import {
  UNIT_OPTIONS,
  archiveChips,
  clearAll,
  filterPlays,
  hasActiveFilters,
  sortPlays,
  type ArchiveQuery,
} from './archive-query';
import { ArchiveTable, PlayCards, PlayDetails, type RowAction } from './archive-table';
import { FilterTool } from './column-menu';
import { ColumnPicker } from './column-picker';
import { PlayEditDialog, type EditMode } from './play-edit-dialog';
import {
  insertPosition,
  resolveDeletePermission,
  seedInsertDraft,
  type InsertSide,
} from './play-edit-model';
import { ResolveDialog } from './resolve-dialog';
import {
  readableError,
  useArchivePlays,
  useArchiveQuery,
  useArchiveVersions,
  useColumnPrefs,
  useElementBox,
  useIsPhoneViewport,
} from './use-archive';
import { VersionsPanel } from './versions-panel';

/** Base font size the column `minWidth`s are designed for. */
const DESIGN_FONT_PX = 13;
/** Width assumed before the first measure (and under jsdom). */
const FALLBACK_WIDTH = 1200;
/** `--arch-cell-px` in archive.css. */
const CELL_PADDING_X = 6;

/** The text a cell draws, padded for its chips / badges, for width measuring. */
function cellMeasureText(column: ArchiveColumn, play: PlayView): string {
  if (!appliesTo(column, play)) return '';
  if (column.id === 'play_number') {
    // `MM` ≈ the star button (an SVG, so not measurable as text) + its gap.
    return `MM ▸${play.play_number} DEF  ${play.conflict_status === 'unresolved' ? ' ⚠2  ' : ''}`;
  }
  if (column.cell === 'tags')
    return tagLabels(play.stats)
      .map((tag) => `${tag}  `)
      .join(' ');
  if (column.cell === 'gain') return `${column.text(play)}  `;
  if (column.cell === 'phase') return 'DEF  ';
  if (column.cell === 'success') return '✓';
  return column.text(play);
}

type Panel =
  | { kind: 'none' }
  | { kind: 'edit'; mode: EditMode; idempotencyKey: string }
  | { kind: 'delete'; play: PlayView }
  | { kind: 'resolve'; play: PlayView };

function newKey(): string {
  return (
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`
  );
}

function liveLabel(status: string): string {
  if (status === 'live') return 'Live';
  if (status === 'connecting') return 'Connecting…';
  if (status === 'polling') return 'Auto-refresh';
  return 'Offline';
}

export interface PlayArchivePageProps {
  /** Injected by tests to drive the live channel deterministically. */
  socketFactory?: HealthSocketFactory;
}

export function PlayArchivePage({ socketFactory }: PlayArchivePageProps = {}): JSX.Element {
  const apiClient = useApiClient();
  const { persona } = useSession();
  const { showToast } = useToast();
  const { activeEvent, activeEventId } = useActiveEvent();
  const isPhone = useIsPhoneViewport();

  // ------------------------------------------------------------ registry
  const library = useStatOptionLibrary();
  const customCatalog = useCustomStatFields({ includeDisabled: true });
  const registryContext = useMemo<RegistryContext>(
    () => ({
      optionsFor: (category, side) =>
        library
          .optionsFor(category, side)
          .map((option) => ({ value: option.value, label: option.label })),
      customFields: customCatalog.fields,
    }),
    [library, customCatalog.fields]
  );
  const columns = useMemo(() => buildArchiveColumns(registryContext), [registryContext]);
  const byId = useMemo(() => columnById(columns), [columns]);
  const facets = useMemo(() => facetIndex(columns), [columns]);

  // ---------------------------------------------------------- state
  const { query, setQuery, eventIdParam } = useArchiveQuery(facets, columns);
  const eventId = eventIdParam ?? activeEventId;
  const log = useArchivePlays({ apiClient, eventId });
  const versionsState = useArchiveVersions(apiClient);
  const { prefs, setPrefs, isSavedLocallyOnly } = useColumnPrefs(apiClient, columns);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [panel, setPanel] = useState<Panel>({ kind: 'none' });
  const [panelError, setPanelError] = useState<{ message: string; field: string | null } | null>(
    null
  );
  const [deleteReason, setDeleteReason] = useState('');
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const allPlays = log.plays;
  const visiblePlays = useMemo(
    () => sortPlays(filterPlays(allPlays, query, columns, facets), query, byId),
    [allPlays, query, columns, facets, byId]
  );
  const conflictCount = allPlays.filter((play) => play.conflict_status === 'unresolved').length;
  const unitCounts = useMemo(() => {
    const counts: Record<string, number> = { all: allPlays.length };
    for (const phase of PLAY_PHASES) counts[phase] = 0;
    for (const play of allPlays) {
      const phase = phaseOf(play);
      if (phase !== null) counts[phase] = (counts[phase] ?? 0) + 1;
    }
    return counts;
  }, [allPlays]);

  // ------------------------------------------------------------- fit
  const [measureRef, box] = useElementBox<HTMLDivElement>(FALLBACK_WIDTH);
  const scale = box.fontSize > 0 ? box.fontSize / DESIGN_FONT_PX : 1;
  const actionsWidth = Math.ceil(ACTIONS_COLUMN_PX * scale);
  // Content-measured column widths (canvas; skipped under jsdom), so a cell
  // shows its value instead of an ellipsis for 90% of the plays.
  const measured = useMemo(() => {
    const measure = canvasMeasurer();
    if (measure === null || box.fontSize === 0 || box.fontFamily === '') return undefined;
    const size = box.fontSize;
    return measureColumnWidths(
      columns,
      allPlays,
      (column, play: PlayView) => cellMeasureText(column, play),
      measure,
      {
        cellFont: `400 ${size}px ${box.fontFamily}`,
        headerFont: `600 ${size * 0.85}px ${box.fontFamily}`,
        cellPadding: 2 * CELL_PADDING_X + 2,
        headerChrome: 2 * CELL_PADDING_X + size * 1.4,
      }
    );
  }, [columns, allPlays, box.fontSize, box.fontFamily]);
  const fit = useMemo(
    () =>
      fitColumns(columns, prefs, {
        // − 2 px: the table's own left/right border sits outside the columns.
        width: box.width - actionsWidth - 2,
        scale,
        measured,
        isApplicable: (column: ArchiveColumn) =>
          query.unit === 'all' || column.phases === undefined || column.phases.includes(query.unit),
      }),
    [columns, prefs, box.width, actionsWidth, scale, measured, query.unit]
  );
  const shownIds = useMemo(() => new Set(fit.shown.map((entry) => entry.column.id)), [fit.shown]);
  const hiddenByWidth = useMemo(() => new Set(fit.hidden.map((column) => column.id)), [fit.hidden]);
  const phoneChipColumns = useMemo(
    () =>
      prefs.columns
        .filter((entry) => entry.visible)
        .map((entry) => byId.get(entry.id))
        .filter(
          (column): column is ArchiveColumn =>
            column !== undefined && (column.cell === 'catalog' || column.group === 'custom')
        ),
    [prefs.columns, byId]
  );

  // ------------------------------------------------------------ versions
  const { load: loadVersions, clear: clearVersions } = versionsState;
  const needsVersions = (play: PlayView): boolean =>
    play.version_count > 1 || play.conflict_status !== 'none';

  const toggleExpanded = useCallback(
    (play: PlayView): void => {
      clearVersions();
      if (expandedId === play.play_id) {
        setExpandedId(null);
        return;
      }
      setExpandedId(play.play_id);
      if (play.version_count > 1 || play.conflict_status !== 'none') loadVersions(play.play_id);
    },
    [clearVersions, expandedId, loadVersions]
  );

  // ---------------------------------------------------------------- live
  const { patchPlays, reload } = log;
  const expandedRef = useRef(expandedId);
  expandedRef.current = expandedId;
  const onLiveEvent = useCallback(
    (message: PlayArchiveEvent): void => {
      if (message.payload.event_id !== eventId) return;
      if (message.type === LIVE_MESSAGE.PLAY_CONFLICT_DETECTED) {
        const detected = message.payload;
        patchPlays((current) =>
          current.map((play) =>
            play.play_id === detected.play_id
              ? {
                  ...play,
                  conflict_status: 'unresolved' as const,
                  version_count: Math.max(play.version_count, detected.version_count),
                }
              : play
          )
        );
        return;
      }
      if (message.type === LIVE_MESSAGE.PLAY_CONFLICT_RESOLVED) {
        const resolved = message.payload;
        patchPlays((current) =>
          current.map((play) =>
            play.play_id === resolved.play_id
              ? {
                  ...play,
                  conflict_status: 'resolved' as const,
                  active_version_id: resolved.winning_version_id,
                }
              : play
          )
        );
        setPanel((current) => (current.kind === 'resolve' ? { kind: 'none' } : current));
        if (expandedRef.current === resolved.play_id) loadVersions(resolved.play_id);
        return;
      }
      // created / updated / deleted / version_added: the list itself is stale.
      reload();
    },
    [eventId, loadVersions, patchPlays, reload]
  );

  const live = usePlayArchiveSubscription({
    eventId,
    onEvent: onLiveEvent,
    onPoll: reload,
    socketFactory,
  });

  // ------------------------------------------------------------- actions
  const eventStatus = versionsState.versions?.event_status ?? activeEvent?.status ?? 'active';
  const deletePermission = resolveDeletePermission(persona, eventStatus);
  const ordered = useMemo(
    () => allPlays.slice().sort((left, right) => left.play_number - right.play_number),
    [allPlays]
  );

  function openInsert(anchor: PlayView, side: InsertSide): void {
    const index = ordered.findIndex((play) => play.play_id === anchor.play_id);
    const previous = side === 'before' ? (ordered[index - 1] ?? null) : anchor;
    const next = side === 'before' ? anchor : (ordered[index + 1] ?? null);
    setPanelError(null);
    setPanel({
      kind: 'edit',
      idempotencyKey: newKey(),
      mode: {
        kind: 'insert',
        anchor,
        side,
        position: insertPosition(anchor, side),
        seed: seedInsertDraft(columns, previous, next),
      },
    });
  }

  function onRowAction(action: RowAction, play: PlayView): void {
    setPanelError(null);
    if (action === 'edit') {
      setPanel({ kind: 'edit', idempotencyKey: newKey(), mode: { kind: 'edit', play } });
    } else if (action === 'insert-before' || action === 'insert-after') {
      openInsert(play, action === 'insert-before' ? 'before' : 'after');
    } else if (action === 'delete') {
      setDeleteReason('');
      setPanel({ kind: 'delete', play });
    } else if (expandedId !== play.play_id) {
      toggleExpanded(play);
    } else if (needsVersions(play) && versionsState.versions === null) {
      loadVersions(play.play_id);
    }
  }

  /**
   * Team-wide star: flips in place at once (optimistic), then the server's
   * answer is adopted; a failure puts the old state back and says so. Other
   * staff see it through the live `game.play.updated` broadcast.
   */
  const toggleFavorite = useCallback(
    (play: PlayView): void => {
      const next = play.is_favorited !== true;
      const setStar = (playId: string, value: boolean): void =>
        patchPlays((current) =>
          current.map((entry) =>
            entry.play_id === playId ? { ...entry, is_favorited: value } : entry
          )
        );
      setStar(play.play_id, next);
      apiClient
        .setPlayFavorite(play.play_id, next)
        .then((saved) => setStar(saved.play_id, saved.is_favorited === true))
        .catch((cause: unknown) => {
          setStar(play.play_id, !next);
          showToast(readableError(cause).message || STAR_COPY.failed, 'error');
        });
    },
    [apiClient, patchPlays, showToast]
  );

  async function submitEdit(play: PlayView, patch: Record<string, unknown>): Promise<void> {
    setPanelError(null);
    try {
      await apiClient.updatePlay(play.play_id, {
        ...patch,
        expected_version_id: play.active_version_id,
      });
      setPanel({ kind: 'none' });
      showToast(`Play #${play.play_number} updated.`, 'success');
      reload();
    } catch (cause: unknown) {
      setPanelError(readableError(cause));
    }
  }

  async function submitInsert(
    payload: Record<string, unknown> & { position: number },
    idempotencyKey: string
  ): Promise<void> {
    if (eventId === null) return;
    setPanelError(null);
    try {
      await apiClient.insertPlay(eventId, payload, idempotencyKey);
      setPanel({ kind: 'none' });
      showToast(`Play inserted as #${payload.position}. Later plays were renumbered.`, 'success');
      reload();
    } catch (cause: unknown) {
      setPanelError(readableError(cause));
    }
  }

  async function submitDelete(play: PlayView): Promise<void> {
    setPanelError(null);
    try {
      await apiClient.deletePlay(
        play.play_id,
        deletePermission.isReasonRequired ? deleteReason.trim() : undefined
      );
      setPanel({ kind: 'none' });
      if (expandedId === play.play_id) setExpandedId(null);
      showToast(`Play #${play.play_number} deleted. Later plays were renumbered.`, 'success');
      reload();
    } catch (cause: unknown) {
      setPanelError(readableError(cause));
    }
  }

  async function submitResolve(play: PlayView, input: ResolveConflictInput): Promise<void> {
    setPanelError(null);
    try {
      await apiClient.resolvePlayConflict(play.play_id, input);
      setPanel({ kind: 'none' });
      showToast('Conflict resolved. The winning version is now the play of record.', 'success');
      reload();
      loadVersions(play.play_id);
    } catch (cause: unknown) {
      setPanelError(readableError(cause));
    }
  }

  const renderExpanded = (play: PlayView): JSX.Element => (
    <PlayDetails play={play} columns={columns} shownIds={isPhone ? new Set() : shownIds}>
      {needsVersions(play) ? (
        <VersionsPanel
          playNumber={play.play_number}
          versions={versionsState.versions}
          isLoading={versionsState.isLoading}
          error={versionsState.error}
          onRetry={() => loadVersions(play.play_id)}
          onResolve={() => {
            setPanelError(null);
            setPanel({ kind: 'resolve', play });
          }}
          onDismiss={() => {
            setExpandedId(null);
            clearVersions();
          }}
        />
      ) : null}
    </PlayDetails>
  );

  // ---------------------------------------------------------------- render
  if (eventId === null) {
    return (
      <section className="view play-archive">
        <h1 className="view__title">Play Archive</h1>
        <div className="banner banner--info">
          <p className="banner__title">No event selected</p>
          <p>Pick a game or practice from the event switcher to see its play log.</p>
        </div>
      </section>
    );
  }

  const chips = archiveChips(query, facets);
  const update = (patch: Partial<ArchiveQuery>): void => setQuery({ ...query, ...patch });
  const isInitialLoad = log.isLoading && !log.hasLoaded;
  const permissions = {
    canDelete: deletePermission.canEdit,
    canFavorite: canFavoritePlays(persona),
  };

  return (
    <section className={`view play-archive play-archive--${prefs.density}`}>
      <header className="archive-head">
        <h1 className="archive-head__title">Play Archive</h1>
        <p className="archive-head__meta">
          <span className="archive-head__event">{activeEvent?.name ?? 'Selected event'}</span>
          <span className={`archive-live archive-live--${live.status}`} role="status">
            <span className="archive-live__dot" aria-hidden="true" />
            {liveLabel(live.status)}
          </span>
        </p>
        <div className="archive-head__actions">
          <TableExportButton
            isDisabled={!log.hasLoaded}
            source={() =>
              buildArchiveExportSource({
                columns,
                prefs,
                plays: visiblePlays,
                event: activeEvent?.event_id === eventId ? activeEvent : null,
                eventId,
              })
            }
          />
        </div>
      </header>

      <div className="archive-toolbar" role="toolbar" aria-label="Play filters">
        <Segmented
          label="Unit"
          className="archive-unit"
          testId="unit-filter"
          value={query.unit}
          options={UNIT_OPTIONS.map((option) => ({
            value: option.value,
            label: option.value === 'all' ? 'All' : option.label,
            shortLabel: option.short,
            badge: log.hasLoaded ? unitCounts[option.value] : undefined,
          }))}
          onChange={(unit) => update({ unit })}
        />
        <div className="archive-search">
          <label className="visually-hidden" htmlFor="archive-search">
            Search plays
          </label>
          <input
            id="archive-search"
            type="search"
            className="archive-search__input"
            placeholder="Search plays, notes, tags…"
            value={query.search}
            onChange={(event) => update({ search: event.target.value })}
          />
        </div>
        <button
          type="button"
          className="archive-tool archive-tool--conflicts"
          aria-pressed={query.isConflictsOnly}
          onClick={() => update({ isConflictsOnly: !query.isConflictsOnly })}
          disabled={conflictCount === 0 && !query.isConflictsOnly}
          title={
            conflictCount === 0
              ? 'No unresolved conflicts'
              : 'Show only plays whose submissions disagree'
          }
        >
          Conflicts only
          <span
            className={`archive-tool__count${conflictCount > 0 ? ' archive-tool__count--warn' : ''}`}
          >
            {conflictCount}
          </span>
        </button>
        <div className="archive-toolbar__end">
          <FilterTool
            columns={columns}
            query={query}
            onChange={setQuery}
            allPlays={allPlays}
            facets={facets}
          />
          {isPhone ? null : (
            <ColumnPicker
              columns={columns}
              prefs={prefs}
              onChange={setPrefs}
              hiddenByWidth={hiddenByWidth}
              isSavedLocallyOnly={isSavedLocallyOnly}
              isOpen={isPickerOpen}
              onOpenChange={setIsPickerOpen}
            />
          )}
        </div>
      </div>

      <div className="archive-status">
        <span className="archive-status__count" data-testid="result-count" aria-live="polite">
          {log.hasLoaded
            ? hasActiveFilters(query)
              ? `${visiblePlays.length} of ${allPlays.length} plays`
              : `${allPlays.length} play${allPlays.length === 1 ? '' : 's'}`
            : 'Loading plays…'}
        </span>
        {chips.length === 0 ? null : (
          <ul className="archive-chips" aria-label="Applied filters">
            {chips.map((chip) => (
              <li key={chip.id} className="archive-chip">
                <span>{chip.label}</span>
                <button
                  type="button"
                  className="archive-chip__clear"
                  aria-label={`Remove filter ${chip.label}`}
                  onClick={() => setQuery(chip.clear(query))}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
        {hasActiveFilters(query) ? (
          <button type="button" className="archive-clear" onClick={() => setQuery(clearAll(query))}>
            Clear all
          </button>
        ) : null}
        <p className="archive-legend" aria-label="Legend">
          <span className="archive-legend__item archive-legend__item--success">Success</span>
          <span className="archive-legend__item archive-legend__item--failure">Not success</span>
          <span
            className="archive-legend__item archive-legend__item--neutral"
            title="No tint: the team rule could not score the snap (e.g. play type Other, a punt with no kick distance). Hover the dash in Succ. for the reason."
          >
            Not measured
          </span>
          <span className="archive-legend__item archive-legend__item--conflict">Conflict</span>
          <span className="archive-legend__opp">Italic = opponent’s call</span>
        </p>
      </div>

      <div className="archive-grid" ref={measureRef}>
        {log.error !== null && allPlays.length === 0 ? (
          <div className="banner banner--error" role="alert">
            <p className="banner__title">The play log did not load</p>
            <p>{log.error}</p>
            <button type="button" className="btn btn--secondary btn--sm" onClick={reload}>
              Try again
            </button>
          </div>
        ) : isInitialLoad ? (
          <div className="archive-skeleton" aria-busy="true" aria-label="Loading plays">
            {Array.from({ length: 8 }, (_, index) => (
              <span key={index} className="archive-skeleton__row" />
            ))}
          </div>
        ) : allPlays.length === 0 ? (
          <div className="archive-empty">
            <p className="archive-empty__title">No plays logged yet</p>
            <p>Plays logged from Play Entry appear here as they happen.</p>
          </div>
        ) : visiblePlays.length === 0 ? (
          <div className="archive-empty">
            <p className="archive-empty__title">
              {query.isConflictsOnly ? 'No unresolved conflicts' : 'No plays match these filters'}
            </p>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => setQuery(clearAll(query))}
            >
              Clear all filters
            </button>
          </div>
        ) : isPhone ? (
          <PlayCards
            plays={visiblePlays}
            chipColumns={phoneChipColumns}
            expandedId={expandedId}
            onToggle={toggleExpanded}
            onAction={onRowAction}
            permissions={permissions}
            renderExpanded={renderExpanded}
            onToggleFavorite={toggleFavorite}
          />
        ) : (
          <ArchiveTable
            plays={visiblePlays}
            allPlays={allPlays}
            fitted={fit.shown}
            hiddenByWidth={fit.hidden}
            columns={columns}
            query={query}
            onQueryChange={setQuery}
            facets={facets}
            expandedId={expandedId}
            onToggle={toggleExpanded}
            onAction={onRowAction}
            onHideColumn={(id) => setPrefs(toggleColumn(prefs, id))}
            permissions={permissions}
            liveStatus={live.status}
            renderExpanded={renderExpanded}
            actionsWidth={actionsWidth}
            density={prefs.density}
            onShowHidden={() => setIsPickerOpen(true)}
            onToggleFavorite={toggleFavorite}
          />
        )}
      </div>

      {panel.kind === 'edit' ? (
        <PlayEditDialog
          mode={panel.mode}
          columns={columns}
          eventId={eventId}
          eventStatus={eventStatus}
          persona={persona}
          error={panelError?.message ?? null}
          errorField={panelError?.field ?? null}
          onSubmitEdit={submitEdit}
          onSubmitInsert={(payload) => submitInsert(payload, panel.idempotencyKey)}
          onCancel={() => setPanel({ kind: 'none' })}
        />
      ) : null}

      {panel.kind === 'delete' ? (
        deletePermission.canEdit ? (
          <ConfirmDialog
            title={`Delete play #${panel.play.play_number}?`}
            tone="danger"
            confirmLabel={`Delete play #${panel.play.play_number}`}
            consequence={
              <>
                <strong>
                  #{panel.play.play_number} · {timeText(panel.play.stats)} ·{' '}
                  {situationText(panel.play.stats)}
                  {playcallText(panel.play.stats) === '—'
                    ? ''
                    : ` · ${playcallText(panel.play.stats)}`}
                </strong>{' '}
                is removed from the game record and from every stat. Plays #
                {panel.play.play_number + 1} and later move up one number.
              </>
            }
            isConfirmDisabled={
              deletePermission.isReasonRequired &&
              deleteReason.trim().length < EVENT_REASON_MIN_LENGTH
            }
            error={panelError?.message ?? null}
            onConfirm={() => submitDelete(panel.play)}
            onCancel={() => setPanel({ kind: 'none' })}
          >
            {deletePermission.isReasonRequired ? (
              <div className="field">
                <label className="field__label" htmlFor="delete-reason">
                  Reason (required — this event is closed)
                </label>
                <textarea
                  id="delete-reason"
                  className="field__control"
                  rows={2}
                  value={deleteReason}
                  onChange={(event) => setDeleteReason(event.target.value)}
                />
                <p className="field__hint">
                  At least {EVENT_REASON_MIN_LENGTH} characters, filed to the audit log.
                </p>
              </div>
            ) : null}
          </ConfirmDialog>
        ) : (
          <ConfirmDialog
            title={`Play #${panel.play.play_number}`}
            consequence={deletePermission.deniedReason ?? 'You cannot delete plays.'}
            confirmLabel="OK"
            onConfirm={() => setPanel({ kind: 'none' })}
            onCancel={() => setPanel({ kind: 'none' })}
          />
        )
      ) : null}

      {panel.kind === 'resolve' && versionsState.versions !== null ? (
        <ResolveDialog
          playNumber={panel.play.play_number}
          versions={versionsState.versions}
          error={panelError?.message ?? null}
          onSubmit={(input) => submitResolve(panel.play, input)}
          onCancel={() => setPanel({ kind: 'none' })}
        />
      ) : null}
    </section>
  );
}
